import React from 'react';
import TopBar from '../../components/layout/TopBar';
import Footer from '../../components/layout/Footer';

const BookingConfirmationPage = ({ onNavigate, bookingData }) => {
  // Default booking data if not provided
  const defaultBookingData = {
    firstName: 'John',
    lastName: 'Doe',
    email: 'john.doe@example.com',
    phone: '9876543210',
    eventDate: '2024-03-15',
    eventTime: '10:00',
    venue: 'Family Home',
    booking: {
      service: {
        title: "Traditional Hindu Wedding",
        image: "💒"
      },
      package: {
        name: "Basic Package"
      },
      totalPrice: 50000
    }
  };

  const data = bookingData && typeof bookingData === 'object' ? bookingData : defaultBookingData;
  const rawBooking = data.booking && typeof data.booking === 'object'
    ? data.booking
    : {
      service: data.service,
      package: data.package,
      totalPrice: data.totalPrice
    };
  const serviceInfo = rawBooking.service && typeof rawBooking.service === 'object'
    ? { ...defaultBookingData.booking.service, ...rawBooking.service }
    : defaultBookingData.booking.service;
  const packageInfo = rawBooking.package && typeof rawBooking.package === 'object'
    ? { ...defaultBookingData.booking.package, ...rawBooking.package }
    : defaultBookingData.booking.package;
  const totalAmount = Number(rawBooking.totalPrice ?? data.totalPrice ?? defaultBookingData.booking.totalPrice) || defaultBookingData.booking.totalPrice;
  const addOnItems = Array.isArray(rawBooking.addOns) ? rawBooking.addOns : [];
  const computedAddOnTotal = addOnItems.reduce((sum, item) => sum + Number(item?.price || 0), 0);
  const pricingInput = rawBooking.pricing || data.pricing || null;
  const pricingBreakdown = {
    baseLabel: pricingInput?.baseLabel || 'Package Price',
    dakshina: Number(pricingInput?.dakshina ?? packageInfo?.price ?? 0) || 0,
    platformFee: Number(pricingInput?.platformFee ?? 0) || 0,
    addOns: Number(pricingInput?.addOns ?? computedAddOnTotal) || 0,
    total: Number(pricingInput?.total ?? totalAmount) || totalAmount,
    platformFeeNote: pricingInput?.platformFeeNote || '',
    schedule: pricingInput?.schedule || null
  };
  const customerName = [data.firstName, data.lastName].filter(Boolean).join(' ').trim() || data.customerInfo?.name || 'Customer';
  const customerEmail = data.email || data.customerInfo?.email || '-';
  const customerPhone = data.phone || data.customerInfo?.phone || '-';
  const eventDate = data.eventDate || data.date || new Date().toISOString().slice(0, 10);
  const eventTime = data.eventTime || data.time || '-';
  const venue = data.venue || data.customerInfo?.address || 'To be confirmed';
  const backendBooking = data.backendBooking || null;
  const bookingId = backendBooking?.id
    ? `BK${String(backendBooking.id).padStart(6, '0')}`
    : `BK${Date.now().toString().slice(-6)}`;
  const bookingStatus = backendBooking?.status || 'CREATED';

  const resolveBookAgainPage = () => {
    const explicitPage = data.servicePage || data.sourcePage || serviceInfo.servicePage;
    if (typeof explicitPage === 'string' && explicitPage.trim()) {
      return explicitPage;
    }

    const routeHints = String(
      serviceInfo.category || serviceInfo.id || serviceInfo.title || ''
    ).toLowerCase();

    if (/wedding|engagement|mehendi|marriage/.test(routeHints)) return 'wedding-services';
    if (/home|griha|vastu|ganpati|lakshmi/.test(routeHints)) return 'home-pooja-services';
    if (/festival|diwali|navratri|dussehra|karva/.test(routeHints)) return 'festival-services';
    if (/life|mundan|annaprashan|naming|thread/.test(routeHints)) return 'life-events';
    if (/spiritual|katha|hanuman|bhagwat|rudra|satyanarayan/.test(routeHints)) return 'spiritual-services';
    if (/special|birthday|anniversary|havan/.test(routeHints)) return 'special-occasions';

    return 'home';
  };

  const bookAgainPage = resolveBookAgainPage();

  const formatPrice = (price) => {
    const amount = Number(price);
    return `₹${(Number.isFinite(amount) ? amount : 0).toLocaleString('en-IN')}`;
  };

  const formatDate = (dateString) => {
    const parsed = new Date(dateString);
    if (Number.isNaN(parsed.getTime())) {
      return 'Date to be confirmed';
    }

    return parsed.toLocaleDateString('en-IN', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleDownloadReceipt = () => {
    console.log('Downloading receipt...');
    // Here you would typically generate and download a PDF receipt
    alert('Receipt download will be available shortly!');
  };

  const handleAddToCalendar = () => {
    const eventDateObj = new Date(`${eventDate}T${eventTime}`);
    const eventTitle = `${serviceInfo.title} - ${venue}`;
    const eventDetails = `Booking ID: ${bookingId}\nService: ${serviceInfo.title}\nPackage: ${packageInfo.name}`;
    
    // Create Google Calendar URL
    if (Number.isNaN(eventDateObj.getTime())) {
      alert('Event date/time is not available for calendar entry.');
      return;
    }

    const startTime = eventDateObj.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
    const endTime = new Date(eventDateObj.getTime() + 4 * 60 * 60 * 1000).toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
    
    const calendarUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(eventTitle)}&dates=${startTime}/${endTime}&details=${encodeURIComponent(eventDetails)}&location=${encodeURIComponent(venue)}`;
    
    window.open(calendarUrl, '_blank');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* TopBar */}
      <TopBar 
        onLocationClick={(location) => console.log('Location clicked:', location)}
        onUserClick={() => onNavigate && onNavigate('login')}
        onCartClick={() => console.log('Cart clicked')}
        onLogoClick={() => onNavigate && onNavigate('home')}
      />

      {/* Confirmation Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        {/* Success Message */}
        <div className="text-center mb-8">
          <div className="text-6xl mb-4">✅</div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Booking Confirmed!</h1>
          <p className="text-lg text-gray-600">
            Thank you for choosing our services. Your booking has been successfully confirmed.
          </p>
        </div>

        {/* Booking Details Card */}
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden mb-8">
          {/* Header */}
          <div className="bg-gradient-to-r from-green-50 to-green-100 px-6 py-4 border-b border-gray-200">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Booking Details</h2>
                <p className="text-sm text-gray-600">Booking ID: <span className="font-mono font-medium">{bookingId}</span></p>
                {backendBooking?.id && (
                  <p className="text-sm text-gray-600">Server Reference: <span className="font-mono font-medium">{backendBooking.id}</span></p>
                )}
              </div>
              <div className="text-3xl">{serviceInfo.image}</div>
            </div>
          </div>

          {/* Content */}
          <div className="p-6">
            <div className="grid md:grid-cols-2 gap-6">
              {/* Customer Information */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Customer Information</h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Name:</span>
                    <span className="font-medium">{customerName}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Email:</span>
                    <span className="font-medium">{customerEmail}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Phone:</span>
                    <span className="font-medium">{customerPhone}</span>
                  </div>
                </div>
              </div>

              {/* Event Information */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Event Information</h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Service:</span>
                    <span className="font-medium">{serviceInfo.title}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Package:</span>
                    <span className="font-medium">{packageInfo.name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Date:</span>
                    <span className="font-medium">{formatDate(eventDate)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Time:</span>
                    <span className="font-medium">{eventTime}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Venue:</span>
                    <span className="font-medium">{venue}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Payment Information */}
            <div className="border-t border-gray-200 pt-6 mt-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Payment Information</h3>
              <div className="bg-gray-50 rounded-lg p-4">
                {(pricingBreakdown.platformFee > 0 || pricingBreakdown.addOns > 0) && (
                  <div className="space-y-1 mb-3 text-sm">
                    <div className="flex justify-between text-gray-700">
                      <span>{pricingBreakdown.baseLabel || 'Package Price'}</span>
                      <span>{formatPrice(pricingBreakdown.dakshina)}</span>
                    </div>
                    {pricingBreakdown.schedule && (
                      <div className="text-xs text-gray-500">
                        {pricingBreakdown.schedule.pandits} pandit(s) x {pricingBreakdown.schedule.days} day(s) x {formatPrice(pricingBreakdown.schedule.ratePerPanditPerDay)}
                      </div>
                    )}
                    {pricingBreakdown.platformFee > 0 && (
                      <div className="flex justify-between text-gray-700">
                        <span>Platform Fee</span>
                        <span>{formatPrice(pricingBreakdown.platformFee)}</span>
                      </div>
                    )}
                    {pricingBreakdown.addOns > 0 && (
                      <div className="flex justify-between text-gray-700">
                        <span>Add-ons</span>
                        <span>{formatPrice(pricingBreakdown.addOns)}</span>
                      </div>
                    )}
                    {pricingBreakdown.platformFeeNote && (
                      <div className="text-xs text-gray-500 bg-white border border-gray-200 rounded-md p-2 mt-2">
                        {pricingBreakdown.platformFeeNote}
                      </div>
                    )}
                  </div>
                )}
                <div className="flex justify-between items-center">
                  <span className="text-lg font-medium text-gray-900">Total Amount:</span>
                  <span className="text-2xl font-bold text-green-600">{formatPrice(pricingBreakdown.total)}</span>
                </div>
                <div className="text-sm text-gray-600 mt-2">
                  Booking status: <span className="text-green-600 font-medium">{String(bookingStatus).replace(/_/g, ' ')}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Next Steps */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-8">
          <h3 className="text-lg font-semibold text-blue-900 mb-4">What Happens Next?</h3>
          <div className="space-y-3">
            <div className="flex items-start space-x-3">
              <span className="text-blue-600 mt-1">📧</span>
              <div>
                <div className="font-medium text-blue-900">Confirmation Email</div>
                <div className="text-sm text-blue-700">You'll receive a detailed confirmation email within 5 minutes</div>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <span className="text-blue-600 mt-1">📞</span>
              <div>
                <div className="font-medium text-blue-900">Pre-event Call</div>
                <div className="text-sm text-blue-700">Our team will contact you 2-3 days before the event to confirm details</div>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <span className="text-blue-600 mt-1">🎯</span>
              <div>
                <div className="font-medium text-blue-900">Service Delivery</div>
                <div className="text-sm text-blue-700">Our pandit will arrive 30 minutes before the scheduled time</div>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <button
            onClick={handleDownloadReceipt}
            className="flex items-center justify-center px-4 py-3 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <span className="mr-2">📄</span>
            Download Receipt
          </button>
          
          <button
            onClick={handleAddToCalendar}
            className="flex items-center justify-center px-4 py-3 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <span className="mr-2">📅</span>
            Add to Calendar
          </button>
          
          <button
            onClick={() => console.log('Contact support')}
            className="flex items-center justify-center px-4 py-3 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <span className="mr-2">💬</span>
            Contact Support
          </button>
          
          <button
            onClick={() => onNavigate && onNavigate(bookAgainPage)}
            className="flex items-center justify-center px-4 py-3 bg-rose-600 text-white rounded-lg hover:bg-rose-700 transition-colors"
          >
            <span className="mr-2">🔄</span>
            Book Again
          </button>
        </div>

        {/* Support Information */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Need Help?</h3>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-medium text-gray-900 mb-2">Customer Support</h4>
              <div className="space-y-2 text-sm text-gray-600">
                <div className="flex items-center space-x-2">
                  <span>📞</span>
                  <span>+91-9876543210</span>
                </div>
                <div className="flex items-center space-x-2">
                  <span>📧</span>
                  <span>support@poojabooking.com</span>
                </div>
                <div className="flex items-center space-x-2">
                  <span>⏰</span>
                  <span>Available 24/7</span>
                </div>
              </div>
            </div>
            
            <div>
              <h4 className="font-medium text-gray-900 mb-2">Emergency Contact</h4>
              <div className="space-y-2 text-sm text-gray-600">
                <div className="flex items-center space-x-2">
                  <span>🚨</span>
                  <span>+91-9876543211 (Emergency)</span>
                </div>
                <div className="text-xs text-gray-500 mt-2">
                  For urgent issues on the day of service
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Share Feedback CTA */}
        <div className="text-center mt-8">
          <div className="bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-lg p-6">
            <div className="text-3xl mb-3">⭐</div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Share Your Experience</h3>
            <p className="text-gray-600 mb-4">
              Help others by sharing your experience with our services
            </p>
            <button
              onClick={() => console.log('Rate service')}
              className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
            >
              Rate Our Service
            </button>
          </div>
        </div>
      </div>

      {/* Footer */}
      <Footer onNavigate={onNavigate} />
    </div>
  );
};

export default BookingConfirmationPage;

